import { gql } from '@apollo/client';

/**
 * Selections shared by the stage pages and the rule preview, so both tables read
 * a content row — and a label reference — out of exactly the same fields, and
 * `labelCategoryMapper` has one payload shape to map from.
 */

/**
 * The `ContentV2` fields `LabelableContent` is built from
 * (`labelCategoryMapper.toLabelableContent`). `provider.name` is a translation
 * KEY on the wire (`{provider.facebook}`), translated by the repository.
 *
 * `ContentV2` is deliberately NOT normalized (no `keyFields`): the same content
 * comes back under many categories and stages, each a snapshot of that read, and
 * nothing here writes to a content row directly.
 */
export const LABELABLE_CONTENT_FRAGMENT = gql`
  fragment LabelableContentFields on ContentV2 {
    contentId
    title
    objective
    parent {
      contentId
      title
    }
    provider {
      provider_id
      name
    }
    biddingStrategy {
      type
    }
  }
`;

/**
 * A label as a row refers to it (a rule match or the applied label): the entity
 * KEY and nothing else.
 *
 * A row needs no more — it carries ids (`toValueIds`, `appliedLabelId`) and the
 * table names them from the category's labels, so a rename in the list already
 * shows up in every row that refers to the same value.
 *
 * And it must carry no more. `ContentLabelCategoryValue` is normalized, so any
 * mutable field selected here lands in the one entity the category list, the
 * label pickers and the duplicate pre-check read. A stage page or a rule preview
 * is computed server-side from its own snapshot, so an answer sent before a
 * rename can land after it — and a name-only update refetches nothing, so
 * nothing supersedes it: the row's stale `name` would silently take the rename
 * back. (Same reason the stage reads select only `contentLabelCategoryId` on the
 * category itself.)
 */
export const CONTENT_LABEL_VALUE_REF_FRAGMENT = gql`
  fragment ContentLabelValueRef on ContentLabelCategoryValue {
    contentLabelCategoryValueId
  }
`;
